import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { PATH } from '../../../constants/path';
import { CONTEXT_ROOT, STATIC_ROOT } from '../../../constants';
import {
    MODAL_TYPE_LOGGED_OUT,
    MODAL_TYPE_LIMITED_ACCESS
} from '../../../services/modal/constants';
import { FUNNEL_ADDRESS_STEP, FUNNEL_PAYMENT_SELECTION_STEP } from '../../../constants/funnel';

import { resetKyc } from '../../../services/kyc/actions';
import { showModal } from '../../../services/modal/actions';
import { resetLock, resetOrder } from '../../../services/ebon/actions';
import { fetchUserBalance } from '../../../services/account/balance/actions';
import { logoutUser } from '../../../services/authentication/logout/actions';

import { getCashbackAmount } from '../../../utils/ebonUtils';
import { isTokenExpired } from '../../../services/authentication/utils';
import { isLimitedMember } from '../../../services/authentication/selector';
import { convertHtmlToReactDomWithParams } from '../../../utils/htmlUtils';
import { getPartnerIdFromQuery, getPartnerNameFromQuery } from '../../../utils';
import { doCartTotalTtc, mapPartner, mapUserCart } from '../../../utils/cartUtils';
import { convertPartnerNameForUrl, getMerchantCardUrl } from '../../../services/partner/utils';

import Stepper from './Stepper';
import { Logo } from '../../icons/Logo';
import SvgComponent from '../../SvgComponent';
import HeadComponent from '../../HeadComponent';
import LocalizedNavLink from '../../router/LocalizedNavLink';

import EbonErrorModal from '../../funnel/EbonErrorModal';
import ScriptContainer from '../../funnel/ScriptContainer';
import FunnelLeftContainer from '../../funnel/FunnelLeftContainer';
import FunnelRightContainer from '../../funnel/FunnelRightContainer';
import { CheckoutFlowProvider } from '../../funnel/payment-step/payment-methods/checkout-flow/CheckoutFlowContext';

import './ValidationEbonPageStyle.scss';

const ValidationEbonPage = ({
    i18n,
    BRAND_NAME,
    token,
    isAuthenticated,
    isLimited,
    ebon,
    userCart,
    order,
    history,
    partner,
    queryPartnerId,
    queryPartnerName,
    showModal,
    logoutUser,
    fetchUserBalance,
    resetLock,
    resetKyc,
    resetOrder
}) => {
    const [userInfo, setUserInfo] = useState({});
    const [modalData, setModalData] = useState(null);
    const [paymentType, setPaymentType] = useState();
    const [stateOfTransaction, setStateOfTransaction] = useState(FUNNEL_ADDRESS_STEP);
    const [kycNeeded, setKycNeeded] = useState(false);

    const partnerId = partner ? partner.id : queryPartnerId;
    const partnerName = partner ? partner.name : queryPartnerName;
    const merchantCardPath = getMerchantCardUrl({ id: partnerId, name: partnerName }, false, true);

    const amountCashback = getCashbackAmount(ebon.ebons);
    const cartTotalValue = doCartTotalTtc(userCart, amountCashback);

    const isMemberAuthenticated = () => !isTokenExpired(token) && isAuthenticated;

    useEffect(() => {
        if (!isMemberAuthenticated()) {
            new Promise((resolve, reject) => {
                history.push(merchantCardPath);
                resolve();
            }).then(() => logoutUser(() => showModal(MODAL_TYPE_LOGGED_OUT)));
        } else if (isLimited) {
            // membre limité : achat de bons d'achat bloqué, le funnel ne s'affiche pas
            // (couvre l'accès direct par URL) — il reste connecté, pas de logout
            new Promise(resolve => {
                history.push(merchantCardPath);
                resolve();
            }).then(() => showModal(MODAL_TYPE_LIMITED_ACCESS));
        }

        if (userCart.length === 0) {
            history.push(merchantCardPath);
        }
    });

    useEffect(() => {
        fetchUserBalance();

        return () => {
            resetLock();
            resetKyc();
            resetOrder();
            setPaymentType(null);
            setStateOfTransaction(FUNNEL_ADDRESS_STEP);
        };
    }, []);

    return (
        <div className="template template-primary flex-container-v">
            <ScriptContainer />
            <div className="entete minimal" id="entete">
                <div className="flex-container-v">
                    <header className="tetedepage conteneur flex-container-h flex-center-alt">
                        <LocalizedNavLink to={PATH.HOME_PATH} title={BRAND_NAME} className="logo">
                            <Logo />
                        </LocalizedNavLink>
                        <Stepper
                            steps={i18n.STEPS}
                            current={stateOfTransaction === FUNNEL_PAYMENT_SELECTION_STEP ? 1 : 0}
                        />
                    </header>
                </div>
            </div>

            <main role="main" id="ebon-page-container" className="conteneur conteneur-mobile">
                <HeadComponent
                    title={i18n.TITLE}
                    description={i18n.DESCRIPTION}
                    canonicalLink={`${CONTEXT_ROOT}${
                        PATH.EBON_VALIDATION_PATH
                    }/${convertPartnerNameForUrl(partnerName)}-${partnerId}`}
                />

                <Stepper
                    steps={i18n.STEPS}
                    current={stateOfTransaction === FUNNEL_PAYMENT_SELECTION_STEP ? 1 : 0}
                    cssClass="mobileDisplay"
                />

                <div className="goBack">
                    <LocalizedNavLink
                        to={merchantCardPath}
                        className="fakelink"
                        title={convertHtmlToReactDomWithParams(i18n.GO_BACK_TITLE, partnerName)}>
                        <SvgComponent
                            src={`${STATIC_ROOT}/chevron.svg`}
                            width="12"
                            height="12"
                            aria-hidden="true"
                            focusable="false"
                        />
                        {i18n.GO_BACK}
                    </LocalizedNavLink>
                </div>

                {!modalData ? (
                    <CheckoutFlowProvider>
                        <div className="mainContainer">
                            <FunnelLeftContainer
                                partnerId={partnerId}
                                userInfo={userInfo}
                                setUserInfo={setUserInfo}
                                paymentType={paymentType}
                                setPaymentType={setPaymentType}
                                cartTotalValue={cartTotalValue}
                                stateOfTransaction={stateOfTransaction}
                                setStateOfTransaction={setStateOfTransaction}
                                setModalData={setModalData}
                                merchantCardPath={merchantCardPath}
                                kycNeeded={kycNeeded}
                                setKycNeeded={setKycNeeded}
                            />

                            <FunnelRightContainer
                                partnerId={partnerId}
                                userInfo={userInfo}
                                userCart={userCart}
                                stateOfTransaction={stateOfTransaction}
                                setStateOfTransaction={setStateOfTransaction}
                                amountCashback={amountCashback}
                                cartTotalValue={cartTotalValue}
                                paymentType={paymentType}
                                kycNeeded={kycNeeded}
                            />
                        </div>
                    </CheckoutFlowProvider>
                ) : (
                    <EbonErrorModal
                        modalData={modalData}
                        merchantCardPath={merchantCardPath}
                        partnerName={partnerName}
                    />
                )}
            </main>
        </div>
    );
};

const mapStateToProps = (state, ownProps) => {
    const queryPartnerId = getPartnerIdFromQuery(ownProps.match.params.query);
    const queryPartnerName = getPartnerNameFromQuery(ownProps.match.params.query);

    return {
        i18n: state.i18n.page.EBON,
        BRAND_NAME: state.i18n.common.BRAND_NAME,
        token: state.authentication.token,
        isAuthenticated: state.authentication.isAuthenticated,
        isLimited: isLimitedMember(state),
        ebon: state.ebon,
        order: state.ebon.createdOrder,
        userCart: mapUserCart(state.ebon.cart, state.ebon.ebonPartners),
        partner: mapPartner(state.ebon.cart, state.ebon.ebonPartners),
        queryPartnerId: queryPartnerId,
        queryPartnerName: queryPartnerName
    };
};

const mapDispatchToProps = {
    showModal,
    logoutUser,
    fetchUserBalance,
    resetLock,
    resetKyc,
    resetOrder
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ValidationEbonPage));



// WEBPACK FOOTER //
// ./src/components/pages/ebon/ValidationEbonPage.js